import React, { useEffect, useState } from "react";
import {
  fetchUsers,
  deleteUser,
  editUser,
  fetchUserById,
  clearSelectedUser,
} from "../../redux/userSlice";
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import { Link, useNavigate } from "react-router-dom";
import {
  Skeleton,
  Button,
  Modal,
  Pagination,
  Input,
  Form,
  message,
  Radio,
  Select,
  Avatar,
  Space,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { houseSettingsActions } from "../../redux/houseSettingsSlice";
import ReusableTable from "../../components/ReusableTable";

const { Option } = Select;

const UserList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm(); // Add Form instance

  const { houseSettings } = useSelector((state) => state.houseSettings);
  const { users, loading, selectedUser, pagination } = useSelector((state) => state.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const [selectedUserView, setSelectedUserView] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [fileList, setFileList] = useState(null);

  // Define table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            src={record.document || "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"}
            icon={<UserOutlined />}
            size={40}
          />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: 'House Number',
      dataIndex: 'house',
      key: 'house_number',
      render: (house) => house?.house_number || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 300,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)} 
            type="primary"
            size="small"
          />
          <Button 
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record.id)} 
            danger
            size="small"
          />
          <Button 
            icon={<EyeOutlined />} 
            onClick={() => handleShowUser(record)}
            size="small"
          />
          <Button 
            icon={<MailOutlined />} 
            onClick={() => handleNavigation(record)}
            size="small"
          >
            Notice
          </Button>
        </Space>
      ),
    },
  ];

  // Fetch users with pagination
  useEffect(() => {
    dispatch(
      fetchUsers({
        page: currentPage,
        pageSize,
        paginate: true,
      })
    );
    dispatch(houseSettingsActions.getHouseSettings());
  }, [dispatch, currentPage, pageSize]);

  // Update form when selectedUser changes
  useEffect(() => {
    if (selectedUser && isEditModalOpen) {
      form.setFieldsValue({
        house_id: selectedUser.house?.id ? Number(selectedUser.house.id) : "",
        name: selectedUser.name || "",
        email: selectedUser.email || "",
        lease_start_date: selectedUser.lease_start_date || "",
        phone_number: selectedUser.phone_number || "",
        secondary_phone_number: selectedUser.secondary_phone_number || "",
        emergencyContact: selectedUser.emergency_contact || "",
        citizenship_number: selectedUser.citizenship_number || "",
        citizenship_district: selectedUser.citizenship_district || "",
        notes: selectedUser.notes || "",
        status: selectedUser.status ? "1" : "0",
      });
      setPreviewImage(selectedUser?.document || null);
      setFileList(null); // Reset file list
    }
  }, [selectedUser, isEditModalOpen, form]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleShowUser = (user) => {
    setSelectedUserView(user);
    setIsUserOpen(true);
  };

  const handleEdit = async (user) => {
    try {
      setEditingUserId(user.id);
      setIsEditModalOpen(true);
      await dispatch(fetchUserById(user.id)).unwrap();
    } catch (error) {
      message.error("Failed to fetch user details");
      setIsEditModalOpen(false);
      setEditingUserId(null);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        message.error('Please upload a valid file (jpg, jpeg, png, or pdf)');
        e.target.value = null;
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        message.error('File size must be less than 5MB');
        e.target.value = null;
        return;
      }

      setFileList(file);
      const reader = new FileReader();
      reader.onload = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleNavigation = (user) => {
    navigate("/dashboard/notices", {
      state: { noticeType: "private", selectedUser: user },
    });
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this user?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await dispatch(deleteUser(id)).unwrap();
          message.success("User deleted successfully");
          dispatch(fetchUsers({ page: currentPage, pageSize, paginate: true }));
        } catch (error) {
          message.error("Failed to delete user");
        }
      },
    });
  };

  const handleUpdateUser = async () => {
    try {
      // Validate form first
      const values = await form.validateFields();
      
      const formData = new FormData();
      formData.append("house_id", String(values.house_id));
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("phone_number", values.phone_number);
      
      if (values.secondary_phone_number)
        formData.append("secondary_phone_number", values.secondary_phone_number);
      
      if (values.emergencyContact)
        formData.append("emergency_contact", values.emergencyContact);
      
      if (values.citizenship_number)
        formData.append("citizenship_number", values.citizenship_number);
      
      if (values.citizenship_district)
        formData.append("citizenship_district", values.citizenship_district);
      
      // Only append document if a new file was selected
      if (fileList) {
        formData.append("document", fileList);
      }
      
      formData.append("notes", values.notes || "");
      formData.append("lease_start_date", values.lease_start_date);
      formData.append("status", values.status);
      formData.append("_method", "PUT");

      await dispatch(
        editUser({ userId: editingUserId, userData: formData })
      ).unwrap();

      message.success("User updated successfully");
      dispatch(fetchUsers({ page: currentPage, pageSize, paginate: true }));
      handleCloseEditModal();
    } catch (err) {
      if (err.errorFields) {
        // Form validation errors
        message.error("Please fix the validation errors");
      } else if (err.errors) {
        // Backend validation errors
        const errorMessages = Object.values(err.errors).flat();
        errorMessages.forEach(msg => message.error(msg));
      } else {
        message.error("Failed to update user");
      }
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUserId(null);
    form.resetFields();
    dispatch(clearSelectedUser());
    setPreviewImage(null);
    setFileList(null);
  };

  // Custom validators
  const validatePhone = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Phone number is required'));
    }
    if (!/^[0-9]{10}$/.test(value)) {
      return Promise.reject(new Error('Phone number must be exactly 10 digits'));
    }
    return Promise.resolve();
  };

  const validateOptionalPhone = (_, value) => {
    if (value && !/^[0-9]{10}$/.test(value)) {
      return Promise.reject(new Error('Phone number must be exactly 10 digits'));
    }
    return Promise.resolve();
  };

  const validateCitizenship = (_, value) => {
    if (value && !/^[0-9]{2}-[0-9]{2}-[0-9]{2}-[0-9]{5}$/.test(value)) {
      return Promise.reject(new Error('Format: XX-XX-XX-XXXXX (e.g., 01-75-12-12345)'));
    }
    return Promise.resolve();
  };

  return (
    <div className="w-full bg-slate-200 p-6 pb-20 rounded-lg shadow-md">
      <div className="bg-white rounded-lg p-4">
        <div className="mb-4 flex justify-between">
          <h1 className="text-xl font-semibold mb-4">Resident List</h1>
          <Link to="/dashboard/adduser">
            <Button
              type="default"
              className="bg-green-800 text-white hover:bg-green-600"
              icon={<PlusOutlined />}
            >
              Add Resident
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="p-12">
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
          </div>
        ) : (
          <>
            <ReusableTable
              columns={columns}
              data={users}
              rowKey="id"
              loading={loading}
              pagination={false}
              scroll={{ x: 800 }}
            />

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={pagination?.total ?? 0}
              onChange={handlePageChange}
              className="mt-4 text-center"
              showSizeChanger={false}
            />
          </>
        )}

        {/* View User Modal */}
        {isUserOpen && selectedUserView && (
          <Modal
            open={isUserOpen}
            onCancel={() => setIsUserOpen(false)}
            footer={null}
            width={800}
          >
            <div className="p-8">
              <h2 className="text-xl font-bold text-center mb-2">
                {selectedUserView.name}
              </h2>
              <p className="text-center">{selectedUserView.phone_number}</p>
              <div className="mt-8 text-sm font-mono border-gray-400 pb-2 mb-4">
                <img
                  src={
                    selectedUserView.profile_picture_url ||
                    "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
                  }
                  alt="user"
                  className="cursor-pointer object-cover h-80 w-80 mx-auto"
                />
                <p className="mt-4">
                  <strong>Email:</strong> {selectedUserView.email}
                </p>
                <p className="mt-4">
                  <strong>Emergency Contact:</strong>{" "}
                  {selectedUserView.emergency_contact}
                </p>
                {selectedUserView.secondary_phone_number && (
                  <p className="mt-4">
                    <strong>Secondary Phone:</strong>{" "}
                    {selectedUserView.secondary_phone_number}
                  </p>
                )}
                {selectedUserView.citizenship_number && (
                  <p className="mt-4">
                    <strong>Citizenship Number:</strong>{" "}
                    {selectedUserView.citizenship_number}
                  </p>
                )}
                {selectedUserView.citizenship_district && (
                  <p className="mt-4">
                    <strong>Citizenship District:</strong>{" "}
                    {selectedUserView.citizenship_district}
                  </p>
                )}
              </div>
            </div>
          </Modal>
        )}

        {/* Edit User Modal */}
        {isEditModalOpen && (
          <Modal
            open={isEditModalOpen}
            onCancel={handleCloseEditModal}
            onOk={handleUpdateUser}
            title="Edit User"
            confirmLoading={loading}
            width={700}
            okText="Update User"
          >
            <Form layout="vertical" form={form}>
              {/* <Form.Item 
                label="House" 
                name="house_id"
                
              >
                <Select placeholder="Select house">
                  {houseSettings?.map((house) => (
                    <Option key={house.id} value={house.id}>
                      {house.house_number}
                    </Option>
                  ))}
                </Select>
              </Form.Item> */}

              <Form.Item 
                label="Name" 
                name="name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input placeholder="Name" />
              </Form.Item>
              
              <Form.Item 
                label="Email" 
                name="email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="Email" type="email" />
              </Form.Item>
              
              <Form.Item 
                label="Phone Number" 
                name="phone_number"
                rules={[{ validator: validatePhone }]}
              >
                <Input
                  placeholder="98XXXXXXXX"
                  maxLength={10}
                />
              </Form.Item>

              <Form.Item 
                label="Secondary Phone Number (Optional)"
                name="secondary_phone_number"
                rules={[{ validator: validateOptionalPhone }]}
              >
                <Input
                  placeholder="98XXXXXXXX"
                  maxLength={10}
                />
              </Form.Item>

              <Form.Item 
                label="Emergency Contact (Optional)"
                name="emergencyContact"
                rules={[{ validator: validateOptionalPhone }]}
              >
                <Input
                  placeholder="98XXXXXXXX"
                  maxLength={10}
                />
              </Form.Item>

              <Form.Item 
                label="Citizenship Number (Optional)"
                name="citizenship_number"
                rules={[{ validator: validateCitizenship }]}
              >
                <Input
                  placeholder="01-75-12-12345"
                  maxLength={17}
                />
              </Form.Item>

              <Form.Item 
                label="Citizenship District (Optional)"
                name="citizenship_district"
              >
                <Input placeholder="Enter district name" />
              </Form.Item>

              <Form.Item 
                label="Lease Start Date" 
                name="lease_start_date"
                rules={[{ required: true, message: 'Lease start date is required' }]}
              >
                <NepaliDatePicker
                  options={{ calenderLocale: "en", valueLocale: "en" }}
                  className="w-full custom-date-picker"
                  placeholder="YYYY-MM-DD"
                />
              </Form.Item>

              <Form.Item 
                label="Notes (Optional)"
                name="notes"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Additional notes..."
                />
              </Form.Item>

              <Form.Item label="User Image (Optional)">
                {previewImage && (
                  <img
                    src={previewImage}
                    alt="preview"
                    className="mb-4 w-full h-40 object-cover rounded"
                  />
                )}
                <Input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleImageChange}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Allowed formats: JPG, JPEG, PNG, PDF (Max 5MB)
                </p>
              </Form.Item>

              <Form.Item 
                label="Status" 
                name="status"
                rules={[{ required: true, message: 'Status is required' }]}
              >
                <Radio.Group>
                  <Radio value="1">Active</Radio>
                  <Radio value="0">Inactive</Radio>
                </Radio.Group>
              </Form.Item>
            </Form>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default UserList;